// React Query hooks for the task tracker (/sidekick/tasks on sidekick-api).
// A project is a board: every active client has one by default, and a PA can
// make custom projects spanning several clients (a couple sharing a board).
// Each board has fixed sections. A task additionally records who its tracked
// time bills to (`client_uids`) — always a non-empty subset of the project's
// members, with the time divided equally between them.
// Timers are sidekick-only; start auto-stops any other running entry.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { TaskAttachment } from "@/hooks/use-task-attachments";

/** The persisted section values — exactly what the API accepts and returns.
 *
 * Four live sections. The older ones (in_progress, multiweek, on_ice, archived)
 * were folded into these by the task-tracker-v2 migration and the DB now
 * forbids them; the API still coerces them on input so a stale bundle doesn't
 * 422, but nothing here may send one. */
export type TaskSection = "backlog" | "agenda" | "todo_this_week" | "complete";

export const TASK_SECTIONS: TaskSection[] = [
  "backlog",
  "agenda",
  "todo_this_week",
  "complete",
];

/** What the board *renders*. "complete" splits into two columns on the server's
 * `completed_this_week` flag, so a board column is not always a section. Use
 * `columnOf` to go task → column and `sectionForColumn` to go back before any
 * PATCH — "completed_this_week" is never a valid wire value. */
export type BoardColumn = TaskSection | "completed_this_week";

/** Every column, in order: backlog → agenda → this week → done this week →
 * the completed archive (earlier weeks). */
export const BOARD_COLUMNS: BoardColumn[] = [
  "backlog",
  "agenda",
  "todo_this_week",
  "completed_this_week",
  "complete",
];

/**
 * The Focus view: what a sidekick has committed to this week. Nothing else.
 *
 * Deliberately excludes Backlog (not committed yet), Agenda (waiting on a
 * conversation, not on the sidekick), and every completed pile — a view whose
 * point is "what's left" must not fill up with what isn't.
 */
export const FOCUS_COLUMNS: BoardColumn[] = ["todo_this_week"];

/** Map a section value from anywhere (a persisted preference, an old payload)
 * onto a live section, mirroring the API's coercion of the retired values. */
export function liveSection(value: string): TaskSection | null {
  switch (value) {
    case "backlog":
    case "agenda":
    case "todo_this_week":
    case "complete":
      return value;
    case "in_progress":
    case "multiweek":
    case "up_next":
      return "todo_this_week";
    case "on_ice":
      return "backlog";
    case "archived":
      return "complete";
    default:
      return null;
  }
}

/** Which column a task belongs in. */
export function columnOf(task: Task): BoardColumn {
  return task.section === "complete" && task.completed_this_week
    ? "completed_this_week"
    : task.section;
}

/** The wire section a column maps to. */
export function sectionForColumn(column: BoardColumn): TaskSection {
  return column === "completed_this_week" ? "complete" : column;
}

/** Columns a user may move a task INTO. "complete" is absent on purpose: the
 * older-weeks pile is read-only, since dropping there would mean "mark done but
 * pretend it was last week". Completing anything lands in "Completed this week". */
export const SELECTABLE_COLUMNS: BoardColumn[] = BOARD_COLUMNS.filter(
  (c) => c !== "complete",
);

/** Where an un-complete sends a task — reopening this week's work almost always
 * means it's still this week's work. */
export const REOPEN_SECTION: TaskSection = "todo_this_week";

/** Done piles read newest-first; `position` is meaningless across the two
 * completed columns (they share one sequence), so never sort them by it. */
export function completedComparator(a: Task, b: Task): number {
  if (a.completed_at && b.completed_at) {
    if (a.completed_at !== b.completed_at) {
      return a.completed_at < b.completed_at ? 1 : -1;
    }
  } else if (a.completed_at !== b.completed_at) {
    return a.completed_at ? -1 : 1; // nulls last
  }
  return b.id - a.id;
}

const COMPLETED_COLUMNS: BoardColumn[] = ["completed_this_week", "complete"];

export type TaskAssignee = {
  uid: string;
  name: string | null;
  kind: "client" | "sidekick";
};

export type TaskTag = { id: number; name: string };

/** Set when the task backs a duck on the client's Ducks in a Row (client app).
 * Read-only here — the PA app only needs to know it exists. */
export type TaskDuck = {
  id: number;
  area_key: string;
  area_name: string;
  why: string;
  sorted_means: string;
  cadence: string;
};

export type Task = {
  id: number;
  project_id: number;
  project_name: string;
  /** Set on a subtask: its parent, always a top-level task on the same board.
   * Subtasks are full tasks and ride in the same board payload as ordinary
   * rows — the views fold them under their parent (see lib/subtasks). */
  parent_task_id: number | null;
  /** Who the tracked time bills to (>= 1); equal split across them. */
  client_uids: string[];
  title: string;
  description_md: string;
  section: TaskSection;
  position: number;
  start_date: string | null;
  due_date: string | null;
  /** Planned effort in minutes. Null = not estimated, which the weekly rollup
   * counts separately so a partial total isn't read as a complete one. Entirely
   * independent of `tracked_seconds` — the two are compared, never reconciled. */
  estimate_minutes: number | null;
  /** 1=Urgent … 4=Low; null = unset. */
  priority: TaskPriority | null;
  /** Both null or both set. Completing a task with a rule makes the server spawn
   * the next instance — the frontend never computes a date or posts a follow-up. */
  recurrence_freq: RecurrenceFreq | null;
  recurrence_interval: number | null;
  /** The instance this was spawned from (immediate predecessor, not the series
   * root). Null on the first task of a series. */
  recurrence_parent_id: number | null;
  assignee: TaskAssignee | null;
  created_by_uid: string | null;
  created_by_name: string | null;
  tracked_seconds: number;
  has_running_entry: boolean;
  comment_count: number;
  attachment_count: number;
  /** Child-task rollup (done = section complete), so a card can show "2/5"
   * without walking the payload. */
  subtask_count: number;
  subtask_done_count: number;
  /** In the board's tag order. */
  tags: TaskTag[];
  duck: TaskDuck | null;
  created_at: string;
  updated_at: string;
  /** Set while section === "complete", else null. */
  completed_at: string | null;
  /** Server-computed against the SAST calendar week — don't recompute locally. */
  completed_this_week: boolean;
};

export type TaskCreatePayload = {
  title: string;
  description_md?: string;
  section?: TaskSection;
  assignee_uid?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  /** Omit to leave unchanged; send null to clear the estimate. */
  estimate_minutes?: number | null;
  /** Omit to leave unchanged; send null to clear the priority. */
  priority?: TaskPriority | null;
  /** Sending null clears the WHOLE rule (interval included) — stopping a series
   * is one field, not two. Setting a freq with no interval means every 1 period.
   * A recurring task must have a due_date; the server 422s otherwise. */
  recurrence_freq?: RecurrenceFreq | null;
  recurrence_interval?: number | null;
  /** Omit to bill to every client on the project (a subtask: its parent's). */
  client_uids?: string[];
  /** Create-only: makes this a subtask of that top-level task. */
  parent_task_id?: number;
  /** Replaces the task's tags. Omit = unchanged (create: none, or the parent's
   * for a subtask); [] clears. */
  tag_ids?: number[];
};

export type TaskUpdatePayload = Partial<Omit<TaskCreatePayload, "parent_task_id">> & {
  position?: number;
};

export type TaskComment = {
  id: number;
  task_id: number;
  author_uid: string;
  author_name: string | null;
  author_kind: "client" | "sidekick" | "other";
  body_md: string;
  attachments: TaskAttachment[];
  created_at: string;
};

export type TimeEntry = {
  id: number;
  task_id: number;
  sidekick_uid: string;
  started_at: string;
  ended_at: string | null;
  seconds: number;
};

export type RunningTimer = {
  entry: TimeEntry | null;
  task_title: string | null;
  project_id: number | null;
};

export type TimeSummary = {
  total_hours: number;
  this_week_hours: number;
  weekly: { iso_year: number; iso_week: number; hours: number }[];
  per_task: { task_id: number; title: string; section: TaskSection; hours: number }[];
};

export type ProjectSummary = {
  id: number;
  name: string;
  /** A client's own auto-created board — not renamable, not deletable. */
  is_default: boolean;
  default_for_client_uid: string | null;
  clients: BoardClient[];
  archived: boolean;
  open_task_count: number;
  /** Tracked timer hours on THIS board. Unlike the client-level figure on the
   * clients list, it excludes the historical manual log, which is client-keyed
   * and can't be attributed to a project. */
  hours_this_week: number;
  hours_total: number;
  running_task_id: number | null;
};

export type TimeReportTask = {
  task_id: number;
  title: string;
  section: TaskSection;
  seconds: number;
  entry_count: number;
};

export type TimeReportClient = {
  /** Null for time on a task that bills to nobody — shown rather than dropped. */
  client_uid: string | null;
  client_name: string;
  seconds: number;
};

export type TimeReportProject = {
  project_id: number;
  project_name: string;
  seconds: number;
  entry_count: number;
  /** Contracted retainer hours per week across this board's clients. */
  weekly_hours: number | null;
  /** `weekly_hours` pro-rated to the reported window, in seconds. Null for an
   * unbounded window, or when no client here has a retainer figure — both mean
   * "nothing to measure against", never zero allocation. */
  allocated_seconds: number | null;
  tasks: TimeReportTask[];
  /** The same seconds, redistributed: each client's equal share of every task
   * they bill to. Sums back to `seconds` — a second way to read the row, not an
   * addition to it. One entry on a solo board, so check the length before
   * drawing it. */
  clients: TimeReportClient[];
};

/** Cross-project rollup of the signed-in sidekick's OWN entries — unlike
 * TimeSummary, which is per-board and counts every PA's time on it. */
export type TimeReport = {
  from: string | null;
  to: string | null;
  total_seconds: number;
  /** Allocation across the reported boards' DISTINCT clients — so it can be
   * less than the sum of the project rows when a client is on a shared board. */
  allocated_seconds: number | null;
  projects: TimeReportProject[];
  /** The same seconds grouped by client rather than by board, so shared work
   * lands on the people who carry it. Sums to `total_seconds`. */
  clients: TimeReportClientRollup[];
};

export type TimeReportClientRollup = {
  client_uid: string | null;
  client_name: string;
  seconds: number;
  /** The client's OWN retainer — not a board's combined figure, which would be
   * counted twice over on a shared board's members. */
  weekly_hours: number | null;
  allocated_seconds: number | null;
  projects: { project_id: number; project_name: string; seconds: number }[];
};

export type BoardClient = {
  client_uid: string;
  client_name: string;
  /** The client's contracted retainer hours per week — the denominator the
   * weekly estimate rollup divides by. Null when they have no retainer figure;
   * treat that as "no contracted hours", never as zero capacity. */
  weekly_hours: number | null;
  /** This client's ATTRIBUTED hours this SAST week — their own board plus an
   * equal share of every shared task, across every board they're on. The only
   * figure `weekly_hours` can honestly be compared against. */
  hours_this_week: number;
};

export type BoardProject = {
  id: number;
  name: string;
  is_default: boolean;
  /** The board's MEMBER clients — not the union of its tasks' `client_uids`,
   * which is billing attribution and omits members with no tasks yet. */
  client_uids: string[];
};

export type MasterBoard = {
  tasks: Task[];
  projects: BoardProject[];
  clients: BoardClient[];
};

const BASE = "/sidekick/tasks";
export const TASK_PROJECTS_KEY = ["task-projects"];
export const RUNNING_TIMER_KEY = ["running-timer"];
export const taskBoardKey = (projectId: number | string) => ["task-board", projectId];
// "master" is never a real project id; sharing the "task-board" prefix means
// useInvalidateTimer's broad invalidation refreshes the master view too.
export const MASTER_BOARD_KEY = ["task-board", "master"];
export const taskCommentsKey = (taskId: number) => ["task-comments", taskId];
export const taskTagsKey = (projectId: number) => ["task-tags", projectId];
export const timeEntriesKey = (taskId: number) => ["task-time-entries", taskId];
export const timeSummaryKey = (projectId: number | string) => [
  "task-time-summary",
  projectId,
];
export const TIME_REPORT_PREFIX = "task-time-report";
export const timeReportKey = (from?: string, to?: string) => [
  TIME_REPORT_PREFIX,
  from ?? "all",
  to ?? "all",
];

export const clientTimeSummaryKey = (clientUid: string) => [
  "client-time-summary",
  clientUid,
];

/** `enabled` exists for the header, which is mounted on every page but only
 * needs this list on a project route — subscribing unconditionally would fetch
 * the whole roster behind Dashboard, Billing and everything else. */
export function useTaskProjects(enabled = true) {
  return useQuery({
    enabled,
    queryKey: TASK_PROJECTS_KEY,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/projects`);
      return (await res.json()) as { projects: ProjectSummary[] };
    },
  });
}

/** One client's attributed hours across every board they're on — a task shared
 * with someone else contributes only their share. Distinct from useTimeSummary,
 * which is a whole-board total. */
export function useClientTimeSummary(clientUid: string | undefined) {
  return useQuery({
    queryKey: clientTimeSummaryKey(clientUid ?? ""),
    enabled: !!clientUid,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/clients/${clientUid}/time-summary`);
      return (await res.json()) as TimeSummary;
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; client_uids: string[] }) => {
      const res = await apiRequest("POST", `${BASE}/projects`, payload);
      return (await res.json()) as ProjectSummary;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASK_PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      ...payload
    }: {
      projectId: number;
      name?: string;
      client_uids?: string[];
      archived?: boolean;
    }) => {
      const res = await apiRequest("PATCH", `${BASE}/projects/${projectId}`, payload);
      return (await res.json()) as ProjectSummary;
    },
    onSuccess: (_data, { projectId }) => {
      qc.invalidateQueries({ queryKey: TASK_PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: taskBoardKey(projectId) });
      qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
    },
  });
}

/** Drop a deleted board out of the projects gallery payload. */
export function withoutProject(
  data: { projects: ProjectSummary[] } | undefined,
  projectId: number,
): { projects: ProjectSummary[] } | undefined {
  if (!data) return data;
  return { ...data, projects: data.projects.filter((p) => p.id !== projectId) };
}

/** Drop a deleted board — and everything that was on it — out of the master
 * rollup. Its tasks go too: they were deleted server-side with the board, and a
 * card whose project no longer exists has no detail sheet to open. */
export function withoutProjectFromMaster(
  data: MasterBoard | undefined,
  projectId: number,
): MasterBoard | undefined {
  if (!data) return data;
  return {
    ...data,
    projects: data.projects.filter((p) => p.id !== projectId),
    tasks: data.tasks.filter((t) => t.project_id !== projectId),
  };
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: number) => {
      await apiRequest("DELETE", `${BASE}/projects/${projectId}`);
      return projectId;
    },
    onSuccess: (_data, projectId) => {
      // Edit the cached payloads, don't just invalidate them. The delete button
      // lives on /tasks/:id but the gallery lives on /tasks, so at this moment
      // `task-projects` has NO mounted observer — invalidateQueries can only
      // flag it stale. The gallery then mounts, renders the cached list (which
      // still contains the deleted board, and `isLoading` is false so there's
      // no skeleton to hide it) and only drops the card when the refetch it
      // kicked off lands. That gap is the stale card the PA sees.
      qc.setQueryData<{ projects: ProjectSummary[] }>(TASK_PROJECTS_KEY, (prev) =>
        withoutProject(prev, projectId),
      );
      qc.setQueriesData<MasterBoard>({ queryKey: MASTER_BOARD_KEY }, (prev) =>
        withoutProjectFromMaster(prev, projectId),
      );
      // The board's own cached queries can never be valid again — evict rather
      // than invalidate. Invalidating would refetch, get a 404, and react-query
      // KEEPS the last good data on error, so a back-button to /tasks/:id would
      // render the full ghost board indefinitely (that page has no error state).
      // Safe to do here: the caller navigates away in the same handler, so the
      // board's observers unmount rather than re-requesting a deleted id.
      qc.removeQueries({ queryKey: taskBoardKey(projectId) });
      qc.removeQueries({ queryKey: timeSummaryKey(projectId) });
      // Still invalidate: the surgery above keeps the UI honest immediately,
      // this reconciles the counts (hours, open totals) that the delete moved.
      qc.invalidateQueries({ queryKey: TASK_PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
    },
  });
}

export function useTaskBoard(projectId: number | undefined) {
  return useQuery({
    queryKey: taskBoardKey(projectId ?? 0),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/projects/${projectId}/board`);
      return (await res.json()) as { tasks: Task[]; clients: BoardClient[] };
    },
  });
}

export function useMasterBoard() {
  return useQuery({
    queryKey: MASTER_BOARD_KEY,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/board`);
      return (await res.json()) as MasterBoard;
    },
  });
}

function useInvalidateBoard(projectId: number) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: taskBoardKey(projectId) });
    qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
    qc.invalidateQueries({ queryKey: TASK_PROJECTS_KEY });
    qc.invalidateQueries({ queryKey: timeSummaryKey(projectId) });
  };
}

export function useCreateTask(projectId: number) {
  const invalidate = useInvalidateBoard(projectId);
  return useMutation({
    mutationFn: async (payload: TaskCreatePayload) => {
      const res = await apiRequest("POST", `${BASE}/projects/${projectId}`, payload);
      return (await res.json()) as Task;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateTask(projectId: number) {
  const qc = useQueryClient();
  const invalidate = useInvalidateBoard(projectId);
  return useMutation({
    mutationFn: async ({ taskId, payload }: { taskId: number; payload: TaskUpdatePayload }) => {
      const res = await apiRequest("PATCH", `${BASE}/${taskId}`, payload);
      return (await res.json()) as Task;
    },
    // Optimistic move: reflect section/position changes immediately so a drag
    // doesn't snap back while the request is in flight. Tag changes too, so a
    // chip appears/disappears on click rather than a round-trip later.
    onMutate: async ({ taskId, payload }) => {
      const moves = payload.section !== undefined || payload.position !== undefined;
      const retags = payload.tag_ids !== undefined;
      if (!moves && !retags) return;
      await qc.cancelQueries({ queryKey: taskBoardKey(projectId) });
      const snapshots = qc.getQueriesData<{ tasks: Task[] }>({
        queryKey: taskBoardKey(projectId),
      });
      const boardTags = qc.getQueryData<{ tags: TaskTag[] }>(taskTagsKey(projectId))?.tags;
      for (const [key, data] of snapshots) {
        if (!data) continue;
        let tasks = moves ? applyOptimisticMove(data.tasks, taskId, payload) : data.tasks;
        if (retags && boardTags) tasks = applyOptimisticTags(tasks, taskId, payload.tag_ids!, boardTags);
        qc.setQueryData(key, { ...data, tasks });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: invalidate,
  });
}

/** Mirror the server's completed_at bookkeeping locally, so a completed card
 * lands in "Completed this week" (and reopening leaves it) without waiting for a
 * refetch. Entering 'complete' NOW is definitionally this week, so no week
 * arithmetic is needed. Returns {} when completion state doesn't change — a
 * position-only reorder must not touch the timestamp. */
function completionPatch(
  task: Task,
  targetSection: TaskSection,
): Partial<Pick<Task, "completed_at" | "completed_this_week">> {
  const wasComplete = task.section === "complete";
  const isComplete = targetSection === "complete";
  if (isComplete === wasComplete) return {};
  return isComplete
    ? { completed_at: new Date().toISOString(), completed_this_week: true }
    : { completed_at: null, completed_this_week: false };
}

/** Replace one task's tags by id, resolving names from the board's tag list
 * (ids it doesn't know are skipped — the refetch fills them in). */
export function applyOptimisticTags(
  tasks: Task[],
  taskId: number,
  tagIds: number[],
  boardTags: TaskTag[],
): Task[] {
  const byId = new Map(boardTags.map((t) => [t.id, t]));
  const tags = tagIds.map((id) => byId.get(id)).filter((t): t is TaskTag => t != null);
  return tasks.map((t) => (t.id === taskId ? { ...t, tags } : t));
}

export function applyOptimisticMove(
  tasks: Task[],
  taskId: number,
  payload: TaskUpdatePayload,
): Task[] {
  const moved = tasks.find((t) => t.id === taskId);
  if (!moved) return tasks;
  const targetSection = payload.section ?? moved.section;
  const completion = completionPatch(moved, targetSection);
  const rest = tasks.filter((t) => t.id !== taskId);
  const siblings = rest
    .filter((t) => t.section === targetSection)
    .sort((a, b) => a.position - b.position || a.id - b.id);
  const idx =
    payload.position !== undefined
      ? Math.max(0, Math.min(payload.position, siblings.length))
      : siblings.length;
  const reordered = [
    ...siblings.slice(0, idx),
    { ...moved, section: targetSection, ...completion },
    ...siblings.slice(idx),
  ];
  const renumbered = reordered.map((t, i) => ({ ...t, position: i }));
  const others = rest
    .filter((t) => t.section !== targetSection)
    .map((t, _i, arr) => {
      if (t.section !== moved.section || moved.section === targetSection) return t;
      // Renumber the source column densely too.
      const sourceSiblings = arr
        .filter((s) => s.section === moved.section)
        .sort((a, b) => a.position - b.position || a.id - b.id);
      return { ...t, position: sourceSiblings.indexOf(t) };
    });
  return [...others, ...renumbered];
}

/** Section-only move for the master (all-boards) view. No position is sent —
 * the server appends to the end of that board's own destination column, so
 * positions across different boards never collide. Optimistic on the master
 * cache only; the board's own cache is invalidated on settle. */
export function useMasterMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      section,
    }: {
      taskId: number;
      projectId: number;
      section: TaskSection;
    }) => {
      const res = await apiRequest("PATCH", `${BASE}/${taskId}`, { section });
      return (await res.json()) as Task;
    },
    onMutate: async ({ taskId, section }) => {
      await qc.cancelQueries({ queryKey: MASTER_BOARD_KEY });
      const snapshots = qc.getQueriesData<MasterBoard>({ queryKey: MASTER_BOARD_KEY });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          tasks: data.tasks.map((t) =>
            t.id === taskId ? { ...t, section, ...completionPatch(t, section) } : t,
          ),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, { projectId }) => {
      qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
      qc.invalidateQueries({ queryKey: taskBoardKey(projectId) });
      qc.invalidateQueries({ queryKey: TASK_PROJECTS_KEY });
    },
  });
}

export function useDeleteTask(projectId: number) {
  const invalidate = useInvalidateBoard(projectId);
  return useMutation({
    mutationFn: async (taskId: number) => {
      await apiRequest("DELETE", `${BASE}/${taskId}`);
    },
    onSuccess: invalidate,
  });
}

export function useTaskComments(taskId: number | undefined) {
  return useQuery({
    queryKey: taskCommentsKey(taskId ?? 0),
    enabled: !!taskId,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/${taskId}/comments`);
      return (await res.json()) as { comments: TaskComment[] };
    },
  });
}

export function useAddComment(projectId: number, taskId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body_md,
      attachment_ids,
    }: {
      body_md: string;
      attachment_ids?: number[];
    }) => {
      const res = await apiRequest("POST", `${BASE}/${taskId}/comments`, {
        body_md,
        attachment_ids: attachment_ids ?? [],
      });
      return (await res.json()) as TaskComment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskCommentsKey(taskId) });
      qc.invalidateQueries({ queryKey: taskBoardKey(projectId) });
      qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
    },
  });
}

export function useDeleteComment(projectId: number, taskId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: number) => {
      await apiRequest("DELETE", `${BASE}/${taskId}/comments/${commentId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskCommentsKey(taskId) });
      // comment_count rides along on the board payload.
      qc.invalidateQueries({ queryKey: taskBoardKey(projectId) });
      qc.invalidateQueries({ queryKey: MASTER_BOARD_KEY });
    },
  });
}

// --- Tags ---------------------------------------------------------------------
// Per board. The server seeds a default set the first time a board's list is
// read, so this is never empty for long.
export function useBoardTags(projectId: number | undefined) {
  return useQuery({
    queryKey: taskTagsKey(projectId ?? 0),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/projects/${projectId}/tags`);
      return (await res.json()) as { tags: TaskTag[] };
    },
  });
}

/** 409 on a duplicate name (case-insensitive); max TAG_NAME_MAX chars. */
export function useCreateTag(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", `${BASE}/projects/${projectId}/tags`, { name });
      return (await res.json()) as TaskTag;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: taskTagsKey(projectId) }),
  });
}

/** Mirrors the API's tag-name ceiling. */
export const TAG_NAME_MAX = 24;

export function useRunningTimer() {
  return useQuery({
    queryKey: RUNNING_TIMER_KEY,
    // Cheap endpoint; keep the global chip honest even if a timer is started
    // from another device.
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/timer`);
      return (await res.json()) as RunningTimer;
    },
  });
}

function useInvalidateTimer() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: RUNNING_TIMER_KEY });
    qc.invalidateQueries({ queryKey: TASK_PROJECTS_KEY });
    // Boards/summaries show tracked time; refresh whichever are mounted.
    qc.invalidateQueries({ queryKey: ["task-board"] });
    qc.invalidateQueries({ queryKey: ["task-time-summary"] });
    qc.invalidateQueries({ queryKey: ["task-time-entries"] });
  };
}

export function useStartTimer() {
  const invalidate = useInvalidateTimer();
  return useMutation({
    mutationFn: async (taskId: number) => {
      const res = await apiRequest("POST", `${BASE}/${taskId}/timer/start`);
      return (await res.json()) as TimeEntry;
    },
    onSuccess: invalidate,
  });
}

export function useStopTimer() {
  const invalidate = useInvalidateTimer();
  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `${BASE}/timer/stop`);
      return (await res.json()) as TimeEntry;
    },
    onSuccess: invalidate,
  });
}

export function useTimeEntries(taskId: number | undefined) {
  return useQuery({
    queryKey: timeEntriesKey(taskId ?? 0),
    enabled: !!taskId,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/${taskId}/time-entries`);
      return (await res.json()) as { entries: TimeEntry[] };
    },
  });
}

export function useCreateTimeEntry(taskId: number) {
  const invalidate = useInvalidateTimer();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { started_at: string; ended_at: string }) => {
      const res = await apiRequest("POST", `${BASE}/${taskId}/time-entries`, payload);
      return (await res.json()) as TimeEntry;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntriesKey(taskId) });
      invalidate();
    },
  });
}

export function useUpdateTimeEntry(taskId: number) {
  const invalidate = useInvalidateTimer();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entryId,
      payload,
    }: {
      entryId: number;
      payload: { started_at?: string; ended_at?: string };
    }) => {
      const res = await apiRequest("PATCH", `${BASE}/time-entries/${entryId}`, payload);
      return (await res.json()) as TimeEntry;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntriesKey(taskId) });
      invalidate();
    },
  });
}

export function useDeleteTimeEntry(taskId: number) {
  const invalidate = useInvalidateTimer();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: number) => {
      await apiRequest("DELETE", `${BASE}/time-entries/${entryId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntriesKey(taskId) });
      invalidate();
    },
  });
}

/** Whole-board total for one project — every client on it, NOT divided. The
 * per-client (split) figure lives on the client-details Time tab. */
export function useTimeSummary(projectId: number | undefined) {
  return useQuery({
    queryKey: timeSummaryKey(projectId ?? 0),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/projects/${projectId}/time-summary`);
      return (await res.json()) as TimeSummary;
    },
  });
}

/** Time report for a window. Omit either bound for open-ended / all time. */
export function useTimeReport(range: { from?: string; to?: string }) {
  const { from, to } = range;
  return useQuery({
    queryKey: timeReportKey(from, to),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      const res = await apiRequest("GET", `${BASE}/time-report${qs ? `?${qs}` : ""}`);
      return (await res.json()) as TimeReport;
    },
  });
}

// --- Presentation helpers shared by the task views ---------------------------
// Keyed by BoardColumn, so it covers both the real sections (needed by the time
// summary / report rows, which key off task.section) and the derived column.
// `bar` is the filled timeline bar; it carries white text, so the two greys that
// work as an 8px dot are not usable there. That knowledge used to live in a
// second `switch` inside task-timeline-view, whose `default:` branch quietly
// rendered every unrecognised section as the primary colour — which is how
// backlog and "to do this week" ended up identical, and how a new section would
// have silently joined them. One map, both consumers, no default branch.
//
// `chip` is the list view's status pill — a tint pair, not a dot, because a pill
// that only carried a dot would be indistinguishable from a plain label at 11px.
// `emptyHint` is what a column says when it has nothing in it: a dashed box
// reading "Waiting on a conversation" teaches what Agenda is FOR, which a blank
// drop zone never did.
//
// Class strings must stay literal for Tailwind's scanner to emit them.
export const SECTION_META: Record<
  BoardColumn,
  {
    label: string;
    dot: string;
    bar: string;
    chipBg: string;
    chipText: string;
    emptyHint: string;
  }
> = {
  backlog: {
    label: "Backlog", dot: "bg-slate-400",
    bar: "bg-slate-400 hover:bg-slate-500",
    chipBg: "bg-slate-100 dark:bg-slate-500/20",
    chipText: "text-slate-600 dark:text-slate-300",
    emptyHint: "Nothing parked here",
  },
  agenda: {
    label: "Agenda", dot: "bg-teal-600",
    bar: "bg-teal-600 hover:bg-teal-700",
    chipBg: "bg-teal-100 dark:bg-teal-500/20",
    chipText: "text-teal-700 dark:text-teal-300",
    emptyHint: "Waiting on a conversation",
  },
  todo_this_week: {
    label: "To do this week", dot: "bg-blue-500",
    bar: "bg-primary hover:bg-blue-600",
    chipBg: "bg-blue-100 dark:bg-blue-500/20",
    chipText: "text-blue-700 dark:text-blue-300",
    emptyHint: "Nothing committed this week",
  },
  completed_this_week: {
    label: "Completed this week", dot: "bg-emerald-500",
    bar: "bg-emerald-500 hover:bg-emerald-600",
    chipBg: "bg-emerald-100 dark:bg-emerald-500/20",
    chipText: "text-emerald-700 dark:text-emerald-300",
    emptyHint: "Nothing finished yet this week",
  },
  complete: {
    label: "Completed archive", dot: "bg-emerald-800",
    bar: "bg-emerald-500 hover:bg-emerald-600",
    chipBg: "bg-emerald-100 dark:bg-emerald-500/20",
    chipText: "text-emerald-800 dark:text-emerald-300",
    emptyHint: "Nothing from earlier weeks",
  },
};

/** A stored section's label where there is no `completed_this_week` flag to
 * split on (time-summary / time-report rows). "Completed archive" would claim a
 * task finished today is from an earlier week, so `complete` reads plainly. */
export function sectionLabel(section: string): string {
  if (section === "complete") return "Complete";
  const live = liveSection(section);
  return live ? SECTION_META[live].label : section;
}

/** 1 = most urgent. Null = unset, which is a distinct state from Normal: an
 * untriaged task and one somebody looked at and called Normal are different
 * facts, and the board filter offers "no priority" as its own option. */
export type TaskPriority = 1 | 2 | 3 | 4;

export const PRIORITY_LEVELS: TaskPriority[] = [1, 2, 3, 4];

/** The single source of truth for how a priority looks. Consumed by the card,
 * the detail sheet, the create dialog, the list view and the filter menu.
 *
 * `rail` is a BACKGROUND class, not a border: the card paints it as a 2px strip
 * clipped by the card's own radius, which a border-left can't do. It is empty
 * below High on purpose — a board where every card wears a colour teaches
 * nothing, so the rail means "this needs looking at before the others". The
 * dot + label in the meta row is the second channel, because colour alone fails
 * colour-vision deficiency and greyscale. */
export const PRIORITY_META: Record<
  TaskPriority,
  { label: string; rail: string; dot: string; text: string }
> = {
  1: {
    label: "Urgent", rail: "bg-red-500", dot: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
  },
  2: {
    label: "High", rail: "bg-amber-500", dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
  },
  3: {
    label: "Normal", rail: "", dot: "bg-slate-400",
    text: "text-muted-foreground",
  },
  4: {
    label: "Low", rail: "",
    dot: "bg-slate-300 dark:bg-slate-600", text: "text-muted-foreground",
  },
};

/** Only these earn a mark on a card. See PRIORITY_META. */
export const FLAGGED_PRIORITIES: TaskPriority[] = [1, 2, 4];

/** Repeat rules the API accepts. 'weekdays' is pinned to interval 1 server-side
 * (CHECK chk_tasks_recurrence_interval) — "every other business day" is nobody's
 * mental model — so the UI must hide the interval control for it or the request
 * 422s. */
export type RecurrenceFreq = "daily" | "weekdays" | "weekly" | "monthly" | "yearly";

export const RECURRENCE_FREQS: RecurrenceFreq[] = [
  "daily",
  "weekdays",
  "weekly",
  "monthly",
  "yearly",
];

export function formatSeconds(total: number): string {
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours === 0 && minutes === 0) return "0m";
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
}

/** Longest single entry a PA can type — anything more is a typo ("300h"). */
export const MAX_ENTRY_SECONDS = 24 * 60 * 60;

/** Ceiling for an ESTIMATE (1000 hours), mirroring ESTIMATE_MINUTES_MAX on the
 * API. Far looser than MAX_ENTRY_SECONDS on purpose: a time entry is one
 * sitting, but a long-running task can legitimately be estimated at weeks. */
export const MAX_ESTIMATE_SECONDS = 60_000 * 60;

// "3h", "3 hr", "1h30", "1h 30m", "90m", "1.5h" — the inverse of formatSeconds.
// Hours and minutes are matched independently so order and spacing are free.
// Longest spellings first, and a not-a-letter lookahead rather than \b — in
// "1h30" the h and the 3 are both word chars, so \b would never match there.
const HOURS_RE = /(\d+(?:[.,]\d+)?)\s*(?:hours|hour|hrs|hr|h)(?![a-z])/i;
const MINUTES_RE = /(\d+(?:[.,]\d+)?)\s*(?:minutes|minute|mins|min|m)(?![a-z])/i;
// "1h30" — a bare number trailing an hours token is minutes, as in ClickUp.
const BARE_MINUTES_RE = /(?:hours|hour|hrs|hr|h)\s*(\d+)\s*$/i;

/**
 * Parse a typed duration into seconds, or null if it isn't one.
 *
 * A unit is required: a bare "45" is rejected rather than guessed at, because
 * reading it as 45 *hours* would silently log a week of work. Clamped to
 * [1 minute, maxSeconds] so a fat-fingered entry can't skew the hours
 * that feed billing.
 *
 * `maxSeconds` defaults to the time-entry ceiling; estimates pass the looser
 * MAX_ESTIMATE_SECONDS via parseEstimateMinutes.
 */
export function parseDuration(
  input: string,
  maxSeconds: number = MAX_ENTRY_SECONDS,
): number | null {
  const raw = input.trim();
  if (!raw) return null;

  const num = (m: RegExpMatchArray | null): number =>
    m ? parseFloat(m[1].replace(",", ".")) : 0;

  const hoursMatch = raw.match(HOURS_RE);
  const minutesMatch = raw.match(MINUTES_RE);
  if (!hoursMatch && !minutesMatch) return null;

  let minutes = num(minutesMatch);
  if (hoursMatch && !minutesMatch) {
    // "1h30" — trailing bare number after the hours token.
    minutes = num(raw.match(BARE_MINUTES_RE));
  }
  const seconds = Math.round(num(hoursMatch) * 3600 + minutes * 60);

  if (seconds < 60 || seconds > maxSeconds) return null;
  return seconds;
}

/** Parse a typed estimate ("3h 30m") into whole minutes, or null if it isn't a
 * duration. Same grammar as a time entry, wider ceiling. */
export function parseEstimateMinutes(input: string): number | null {
  const seconds = parseDuration(input, MAX_ESTIMATE_SECONDS);
  return seconds === null ? null : Math.round(seconds / 60);
}

/** Render minutes in the same "1h 30m" idiom as tracked time, so an estimate
 * and its actual can be read side by side without unit-switching. */
export function formatEstimate(minutes: number): string {
  return formatSeconds(minutes * 60);
}

/** Bucket tasks into board columns. Every column is present (empty ones render
 * as empty).
 *
 * `compare` is ignored for the two completed columns: they share one `position`
 * sequence, so ordering them by anything but `completed_at` is meaningless — and
 * a caller-supplied comparator (the master view sorts by due date) would put
 * finished work in due-date order. */
export function groupByColumn(
  tasks: Task[],
  compare?: (a: Task, b: Task) => number,
): Record<BoardColumn, Task[]> {
  const columns = BOARD_COLUMNS;
  const grouped = Object.fromEntries(
    columns.map((c) => [c, [] as Task[]]),
  ) as unknown as Record<BoardColumn, Task[]>;
  // `?.` — a row carrying a section this bundle doesn't know (a retired value
  // from a stale cache) is dropped rather than crashing the board.
  for (const task of tasks) grouped[columnOf(task)]?.push(task);
  const byPosition = (a: Task, b: Task) => a.position - b.position || a.id - b.id;
  for (const c of columns) {
    grouped[c].sort(
      COMPLETED_COLUMNS.includes(c) ? completedComparator : compare ?? byPosition,
    );
  }
  return grouped;
}
